import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [walletStatus, setWalletStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsResponse, walletResponse] = await Promise.all([
        api.get('/newsletter/subscribers/count'),
        api.get('/newsletter/wallet-status')
      ]);

      setStats(statsResponse.data);
      setWalletStatus(walletResponse.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="dashboard-loading">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="dashboard-error">
          <p>❌ {error}</p>
          <button onClick={fetchDashboardData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const walletPercentage = ((walletStatus?.wallets_collected / walletStatus?.max_wallets) * 100).toFixed(1);

  return (
    <div className="admin-dashboard">
      <div className="dashboard-container">
        
        {/* Header */}
        <div className="dashboard-header">
          <h1>Newsletter Dashboard</h1>
          <button onClick={fetchDashboardData} className="refresh-button">
            🔄 Refresh
          </button>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          
          {/* Total Subscribers */}
          <div className="stat-card primary">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <h3>Total Subscribers</h3>
              <p className="stat-number">{stats?.total_subscribers || 0}</p>
              <p className="stat-label">Confirmed subscribers</p>
            </div>
          </div>

          {/* Wallets Collected */}
          <div className="stat-card success">
            <div className="stat-icon">🎁</div>
            <div className="stat-content">
              <h3>Dogecoin Wallets</h3>
              <p className="stat-number">
                {walletStatus?.wallets_collected || 0} / {walletStatus?.max_wallets || 100}
              </p>
              <p className="stat-label">{walletPercentage}% of limit</p>
            </div>
          </div>

          {/* Wallet Progress */}
          <div className="stat-card wallet-progress">
            <h3>Wallet Collection Progress</h3>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${walletPercentage}%` }}
              ></div>
            </div>
            <p className="progress-text">
              {walletStatus?.remaining || 0} slots remaining
            </p>
            {walletStatus?.wallet_collection_active ? (
              <span className="status-badge active">✅ Active</span>
            ) : (
              <span className="status-badge inactive">🔒 Full</span>
            )}
          </div>

          {/* Top Countries */}
          <div className="stat-card countries">
            <h3>Top Countries</h3>
            {stats?.top_countries && stats.top_countries.length > 0 ? (
              <div className="countries-list">
                {stats.top_countries.map((country, index) => (
                  <div key={index} className="country-item">
                    <span className="country-name">
                      {country.country || 'Unknown'}
                    </span>
                    <span className="country-count">{country.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No country data available</p>
            )}
          </div>

        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <h3>Quick Actions</h3>
          <div className="actions-grid">
            <button className="action-button" onClick={() => window.location.href = '/'}>
              🏠 View Website
            </button>
            <button className="action-button" onClick={() => window.location.href = '/unsubscribe'}>
              📧 Unsubscribe Page
            </button>
            <button className="action-button" onClick={fetchDashboardData}>
              📊 Refresh Data
            </button>
          </div>
        </div>

        {/* Last Updated */}
        <div className="dashboard-footer">
          <p>Last updated: {new Date().toLocaleString()}</p>
          <p className="auto-refresh">Auto-refreshes every 30 seconds</p>
        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;
